<!--
  File name: AnalyticsTable.vue
  Program Description:
    - This component includes the tables used for navigating through the IPCR and DPCR
    - There are two tables with different data:
      * Table for roles and time period
      * Table for users (Only in DPCR)
-->
<template>
  <div class="page">
    <v-container
      class="container"
    >
      <v-row>
        <v-col>
          <v-card
            tile
            flat
            color="transparent"
          >
            <v-toolbar
              color="transparent"
              flat
            >
              <v-toolbar-title class="text-h4 font-weight-bold px-0">
                {{ pageHeader }}
              </v-toolbar-title>
              <v-spacer />
            </v-toolbar>
          </v-card>
        </v-col>
      </v-row>
      <!-- roles table -->
      <template v-if="showRolesTable">
        <v-row justify="space-between">
          <v-col
            class="py-0"
          >
            <template
              v-if="dpcrRolesView"
            >
              <v-btn
                text
                color="primary"
                @click="reinit"
              >
                <v-icon class="mr-2">
                  mdi-arrow-left
                </v-icon>
                Select Another User
              </v-btn>
            </template>
          </v-col>
          <v-col
            class="py-0"
            cols="6"
            style="display: flex; gap: 1.25rem"
          >
            <v-col
              cols="4"
              class="pa-0"
            >
              <v-select
                v-model="year"
                :items="yearItems"
                label="Year"
                dense
                solo
                :loading="isLoading"
                :disabled="isLoading"
                background-color="white"
              />
            </v-col>
            <v-col class="pa-0">
              <v-select
                v-model="semester"
                :items="semItems"
                label="Semester"
                dense
                solo
                :loading="isLoading"
                :disabled="isLoading"
                background-color="white"
              >
                <template v-slot:item="{ item }">
                  <span class="mr-1">{{ item }}</span>
                  <span
                    v-if="item === '1st Semester'"
                    class="grey--text"
                  >
                    (Jan - Jun)
                  </span>
                  <span
                    v-if="item === '2nd Semester'"
                    class="grey--text"
                  >
                    (Jul - Dec)
                  </span>
                </template>
                <template v-slot:selection="{ item }">
                  <span class="mr-1">{{ item }}</span>
                  <span
                    v-if="item === '1st Semester'"
                    class="grey--text"
                  >
                    (Jan - Jun)
                  </span>
                  <span
                    v-if="item === '2nd Semester'"
                    class="grey--text"
                  >
                    (Jul - Dec)
                  </span>
                </template>
              </v-select>
            </v-col>
          </v-col>
        </v-row>
        <v-row class="flex-grow-1">
          <v-col class="pt-0">
            <v-card
              tile
            >
              <v-card-text>
                <v-data-table
                  item-key="actionType"
                  :headers="headers"
                  :items="tableItems"
                  :loading="isLoading"
                  hide-default-footer
                  show-expand
                  single-expand
                >
                  <template
                    v-slot:[`item.status`]="{ item }"
                  >
                    <StatusChip
                      :status="getRoleProperties(item.status)"
                      :label="item.status"
                    />
                  </template>
                  <template v-slot:[`item.action`]="{ item }">
                    <template v-if="item.analyticsAndRatingId || showViewBtn">
                      <router-link
                        :to="{
                          name: 'AnalyticsForm',
                          params: {
                            dataTab,
                            operation: item.analyticsAndRatingId ? 'view' : 'defaultView',
                            role: roleAction(item.actionType),
                            year,
                            semester: semester.substring(0,3),
                            id: item.analyticsAndRatingId || (isIpcr ? $s.user : userId)
                          },
                          query : {user: isIpcr ? $s.user : userId}
                        }"
                      >
                        <v-btn
                          :id="`analytics-view-btn`"
                          icon
                          class="mr-8"
                          color="primary"
                        >
                          <v-icon class="mr-2">
                            mdi-eye
                          </v-icon>
                          View
                        </v-btn>
                      </router-link>
                    </template>
                    <template v-if="forEvaluation && !item.analyticsAndRatingId">
                      <router-link
                        :to="{
                          name: 'AnalyticsForm',
                          params: {
                            dataTab,
                            operation: isIpcr ? 'create' : 'defaultView',
                            role: roleAction(item.actionType),
                            year,
                            semester: semester.substring(0,3),
                            id: item.analyticsAndRatingId || (isIpcr ? $s.user : userId),

                          },
                          query: {user: isIpcr ? $s.user : userId}
                        }"
                      >
                        <v-btn
                          :id="`analytics-create-btn`"
                          :icon="!isIpcr"
                          color="primary"
                          :class="!isIpcr ? 'mr-8' : ''"
                        >
                          <v-icon class="mr-2">
                            {{ isIpcr ? 'mdi-thumb-up-outline' : 'mdi-eye' }}
                          </v-icon>
                          {{ isIpcr ? 'Rate' : 'View' }}
                        </v-btn>
                      </router-link>
                    </template>
                  </template>
                  <template v-slot:expanded-item="{ item }">
                    <td
                      colspan="12"
                      class="pa-0"
                    >
                      <v-card
                        flat
                        outlined
                      >
                        <v-card-title>
                          DBN Actual Accomplishments
                        </v-card-title>
                      </v-card>
                      <v-container>
                        <template
                          v-for="doc, i in item.tallies"
                        >
                          <v-row
                            :key="i"
                            style="border-bottom: thin solid rgba(0, 0, 0, 0.12)"
                          >
                            <v-col>
                              {{ doc.name }}
                            </v-col>
                            <v-col class="d-flex justify-end">
                              {{ doc.tally }}
                            </v-col>
                          </v-row>
                        </template>
                      </v-container>
                    </td>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
      <!-- end of roles table -->
      <template v-else>
        <v-row class="flex-grow-1">
          <v-col class="pt-0">
            <v-card
              tile
            >
              <v-card-text>
                <v-data-table
                  item-key="_id"
                  :headers="userTableHeaders"
                  :items="userTableItems"
                  :loading="isLoading"
                >
                  <template #top>
                    <v-row justify="end">
                      <v-col
                        cols="6"
                      >
                        <v-text-field
                          v-model="search"
                          dense
                          single-line
                          outlined
                          hide-details
                          label="Search"
                          append-icon="search"
                          @keyup.enter="setUserTable()"
                          @click:append="setUserTable()"
                          @keyup.delete="clearSearch()"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="pt-0">
                        <v-divider />
                      </v-col>
                    </v-row>
                  </template>
                  <template v-slot:[`item.action`]="{item}">
                    <v-btn
                      :id="`analytics-view-btn`"
                      icon
                      color="primary"
                      @click="switchTable(item.id, item.users)"
                    >
                      <v-icon>
                        mdi-eye
                      </v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
      <v-btn
        v-if="showFab"
        fab
        fixed
        bottom
        right
        class="primary"
        @click="scrollToTop()"
      >
        <v-icon
          color="#FFFFFF"
        >
          mdi-arrow-up
        </v-icon>
      </v-btn>
    </v-container>
  </div>
</template>

<script>
import analyticsTable from '@/meta/analyticsTable'
import crud from '@/utils/rest/crud'
import StatusChip from '@/components/StatusChip'

export default {
  name: 'AnalyticsTable',
  components: { StatusChip },
  data: () => ({
    userId: '',
    userName: '',
    yearItems: [],
    year: '',
    semItems: ['1st Semester', '2nd Semester'],
    semester: '1st Semester',
    tableOptions: {},
    dpcrRolesView: false,
    isLoading: false,
    tab: null,
    tableItems: [],
    headers: analyticsTable.rolesHeaders,
    userTableHeaders: analyticsTable.usersHeaders,
    userTableItems: [],
    search: '',
    showFab: false

  }),
  computed: {
    isIpcr () {
      return this.dataTab === 'ipcr'
    },
    pageHeader () {
      return this.dpcrRolesView ? `IPCR - ${this.userName} ` : 'Analytics and Ratings'
    },
    dataTab () {
      return this.$route.params.dataTab
    },
    showRolesTable () {
      return this.dataTab === 'ipcr' || this.dpcrRolesView === true
    },
    currentYear () {
      const date = new Date().getFullYear().toString()
      return date
    },
    prevYear () {
      return (this.currentYear - 1).toString()
    },
    currentSem () {
      const firstSem = [0, 1, 2, 3, 4, 5]
      const secondSem = [6, 7, 8, 9, 10, 11]
      const date = new Date().getMonth()
      if (firstSem.includes(date)) return '1st Semester'
      else if (secondSem.includes(date)) return '2nd Semester'
      return date
    },
    isCurrentSem () {
      return this.currentYear === this.year && this.currentSem === this.semester
    },
    forEvaluation () {
      if (!this.isCurrentSem) {
        if (this.currentYear === this.year) return true
        else {
          if ((this.prevYear === this.year) && this.semester === '2nd Semester') return true
          return false
        }
      }
      return false
    },
    showViewBtn () {
      return (this.isCurrentSem || (!this.isCurrentSem && !this.forEvaluation))
    }
  },
  watch: {
    year () {
      this.populateTable()
    },
    semester () {
      this.populateTable()
    },
    async dataTab () {
      this.reinit()

      if (this.dataTab === 'ipcr') {
        await this.setYears()
      } if (this.dataTab === 'dpcr') {
        await this.setUserTable()
      }
    }
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
  },
  async created () {
    const routeQuery = this.$route.query
    if (this.dataTab === 'ipcr') {
      await this.setYears()
    }
    if (routeQuery.view) {
      this.dataTab === 'dpcr' && (this.dpcrRolesView = true)
      this.userId = routeQuery.user
      this.dataTab === 'dpcr' && (await this.setYears(this.userId))
      this.year = routeQuery.year
      this.semester = routeQuery.sem === '1st' ? '1st Semester' : '2nd Semester'
      if (this.dataTab === 'dpcr') {
        const users = await this.getUsers()
        const currUser = users.find((user) => {
          return user._id === this.userId
        })
        this.userName = currUser.fullName
      }
    }
    if (this.dataTab === 'dpcr' && !this.dpcrRolesView) {
      await this.setUserTable()
    }
  },
  methods: {
    // from users table go to roles table
    async switchTable (id, name) {
      this.dpcrRolesView = true
      this.isLoading = true
      this.userId = id
      this.userName = name
      await this.setYears(id)
    },
    // get filtered users using search
    async searchUsers () {
      this.isLoading = true

      const resp = await crud('actionOwnership/users').list({ search: this.search })

      this.isLoading = false
      return resp.entry
    },
    // properties for the status chips
    getRoleProperties (status) {
      let st = status
      if (status === 'Evaluated') st = 'Submitted'
      else if (status === 'Updated') st = 'Final Review'
      else if (status === 'Late') st = 'Closed'
      else if (status === 'Ongoing Semester') st = 'Initial Order'
      return st
    },
    // get all users with empty search field
    clearSearch () {
      if (!this.search) this.setUserTable()
    },
    // set user table using received user data
    async setUserTable () {
      let users
      if (this.search) {
        users = await this.searchUsers()
      } else {
        users = await this.getUsers()
      }
      const userArr = users.map((user) => {
        return { users: user.fullName, id: user._id }
      })
      this.userTableItems = userArr
    },
    // get all users with actions (evaluate, review, assign)
    async getUsers () {
      this.isLoading = true

      const resp = await crud('actionOwnership/users').list()

      this.isLoading = false
      return resp.entry
    },
    // get years of actions data and set items for year dropdown
    async setYears (id) {
      this.isLoading = true

      let yearList
      if (!id) {
        yearList = await crud('actionOwnership/years').list()
      } else {
        yearList = await crud(`actionOwnership/years/${id}`).list()
      }
      yearList.entry && this.$set(this.$data, 'yearItems', yearList.entry)
      this.yearItems && this.$set(this.$data, 'year', this.yearItems[this.yearItems.length - 1])

      this.isLoading = false
    },
    // adjust data to match router props for AnalyticsForm
    roleAction (role) {
      if (role === 'Evaluator') return 'evaluate'
      else if (role === 'Initial Reviewer') return 'initialReview'
      else if (role === 'Final Reviewer') return 'finalReview'
      else return 'assign'
    },
    // set roles table
    async populateTable () {
      this.isLoading = true
      if (this.dpcrRolesView || this.isIpcr) {
        const sem = this.semester === '1st Semester' ? '1st' : '2nd'
        const extQuery = `year=${this.year}&sem=${sem}`
        let resp
        if (this.userId && this.dataTab === 'dpcr') {
          resp = await crud(`actionOwnership/actionTypes/${this.userId}`).list({ extQuery })
        } else {
          resp = await crud('actionOwnership/actionTypes').list({ extQuery })
        }
        const data = resp.entry.map((i) => {
          switch (i.actionType) {
            case 'evaluate':
              i.actionType = 'Evaluator'
              break
            case 'initialReview':
              i.actionType = 'Initial Reviewer'
              break
            case 'finalReview':
              i.actionType = 'Final Reviewer'
              break
            case 'assign':
              i.actionType = 'Assignor'
              break
            default:
              i.actionType = null
              break
          }
          if (i.analyticsAndRatingId === '') {
            if (this.isCurrentSem) i.status = 'Ongoing Semester'
            else if (this.forEvaluation) i.status = 'Pending'
            else i.status = 'Late'
          } else {
            if (i.status === 'Submitted') i.status = 'Evaluated'
          }
          return i
        })
        data && this.$set(this.$data, 'tableItems', data)
      }

      this.isLoading = false
    },
    // re initialize all variables from $data
    reinit () {
      this.dpcrRolesView = false
      const queryObj = Object.keys(this.$route.query)
      queryObj.length && (this.$router.replace({ query: null }))
      this.userId = ''
      this.userName = ''
      this.tableItems = []
      this.yearItems = []
      this.year = ''
      this.semItems = ['1st Semester', '2nd Semester']
      this.semester = '1st Semester'
      this.setUserTable()
    },
    // fab btn methods
    scrollToTop () {
      this.$vuetify.goTo(0, 0)
    },
    onScroll () {
      this.showFab = window.top.scrollY > 200
    }

  }
}
</script>

<style scoped>

.container {
  max-width: 800px;
}
.shadow{
  box-shadow:4px 8px 8px -2px #c3c3c3 !important;
}
.v-toolbar >>> .v-toolbar__content{
    padding: 0;
}

</style>
